import moment from "moment";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import MaterialTable, { MTableToolbar } from "material-table";
import { ToastContainer } from "react-toastify";
import SlidingPane from "react-sliding-pane";
import "react-sliding-pane/dist/react-sliding-pane.css";
import Select from "react-select";
import axios from "axios";
import CreateTicket from "../tickiting/components/createTicket.js";
import { getTrans as t } from "../../Localization/translations";
import GetCompanyInfoService from "../../services/company/getCompanyInfoService";
import { API } from "../../backend.js";

const statusColors = {
  closed: "bg-warning text-white",
  resolved: "bg-success text-white",
  escalated: "bg-danger bg-gradient text-white",
  open: "bg-info text-dark",
  unassigned: "badge bg-secondary",
  replied: "bg-warning text-dark",
  deleted: "bg-secondary text-white",
  watchlist: "bg-secondary text-white",
};

const priorityColors = {
  Critical: "bg-danger text-white",
  High: "bg-warning text-dark",
  Medium: "bg-info text-dark",
  low: "bg-success text-white",
  normal: "text-dark",
  critical: "bg-danger text-white",
  high: "bg-warning text-dark",
  medium: "bg-info text-dark",
  low: "bg-success text-white",
  normal: "text-dark",
};

const priorityOptions = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const statusOptions = [
  { value: "unassigned", label: "Unassigned" },
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
  { value: "escalated", label: "Escalated" },
];

const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

export default function CompanyTickets() {
  const navigate = useNavigate();
  const location = useLocation();
  const tableTicketRef = useRef();

  const [statusFilter, setStatusFilter] = useState("my-tickets");
  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [ticketPrefill, setTicketPrefill] = useState(null);
  const [fullCompanyName, setFullCompanyName] = useState("");
  const [ticketsData, setTicketsData] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalTickets, setTotalTickets] = useState(0);
  const [paneOpen, setPaneOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerInputValue, setCustomerInputValue] = useState("");
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedCreationDate, setSelectedCreationDate] = useState("");
  const [selectedAssignedTo, setSelectedAssignedTo] = useState(null);
  const [assignedToOptions, setAssignedToOptions] = useState([]);
  const [assignedToInputValue, setAssignedToInputValue] = useState("");
  const [isLoadingAssignedTo, setIsLoadingAssignedTo] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    total: 0,
    new: 0,
    open: 0,
    resolved: 0,
    closed: 0,
    escalated: 0,
  });
  const [selectedTicketType, setSelectedTicketType] = useState("All");
  const [currentFilters, setCurrentFilters] = useState({});

  const [permissions, setPermissions] = useState(localStorage.getItem('permissions'));
  const hasViewAllTicketsPermission = permissions && permissions.split(',').includes('view_all_tickets');


  const filteredTickets = useMemo(() => {
    if (selectedStatus?.value === "closed") {
      return ticketsData.filter((ticket) => ticket.status?.toLowerCase() === "closed");
    }
    return ticketsData.filter((ticket) => ticket.status?.toLowerCase() !== "closed");
  }, [ticketsData, selectedStatus]);

  const userData = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    const customerPermission = localStorage.getItem("customer");
    if (customerPermission === "Customer") {
      navigate("/");
      return;
    }
  }, [navigate]);

  useEffect(() => {
    if (location.state?.openCreateTicket) {
      setTicketPrefill({
        title: location.state.prefillTitle || "",
        description: location.state.prefillDescription || "",
      });
      setShowCreateTicket(true);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      try {
        const response = await GetCompanyInfoService.getCompanyInfo();
        const companyData = response.data;
        const fullName =
          companyData.assigned_to_name ||
          companyData.name ||
          companyData.full_name ||
          companyData.business_name ||
          companyData.legal_name ||
          "Company Name Not Available";
        setFullCompanyName(fullName);
      } catch (err) {
        setFullCompanyName("Company Name Unavailable");
      }
    };
    fetchCompanyDetails();
  }, []);

  useEffect(() => {
    const savedFilters = sessionStorage.getItem("ticketFilters");
    const savedTicketType = sessionStorage.getItem("selectedTicketType");
    const savedStatusFilter = sessionStorage.getItem("statusFilter");

    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      setCurrentFilters(parsedFilters);

      if (parsedFilters.customerName) {
        setSelectedCustomer({
          customer_name: parsedFilters.customerName,
          client_name: parsedFilters.customerName,
          email: parsedFilters.customerName,
        });
      }
      if (parsedFilters.priority) {
        setSelectedPriority(
          priorityOptions.find((option) => option.value === parsedFilters.priority)
        );
      }
      if (parsedFilters.status) {
        setSelectedStatus(
          statusOptions.find((option) => option.value === parsedFilters.status)
        );
      }
      if (parsedFilters.creationDate) {
        setSelectedCreationDate(parsedFilters.creationDate);
      }
      if (parsedFilters.assignedTo) {
        setSelectedAssignedTo({
          assigned_name: parsedFilters.assignedTo,
          assigned_to_name: parsedFilters.assignedTo,
          email: parsedFilters.assignedTo,
        });
      }
    } else {
      setCurrentFilters({});
    }

    if (savedTicketType) {
      setSelectedTicketType(savedTicketType);
    }

    if (savedStatusFilter) {
      setStatusFilter(savedStatusFilter);
    }
  }, []);

  const fetchTickets = debounce(async (filters = {}, retryCount = 0) => {
    setDataLoading(true);
    setError(null);
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const headers = {
        Authorization: `Bearer ${user?.access || user?.token}`,
      };
      let params = [];
      if (filters.customerName) {
        params.push(`client_name=${encodeURIComponent(filters.customerName)}`);
      }
      if (filters.priority) {
        params.push(`priority=${encodeURIComponent(filters.priority)}`);
      }
      if (filters.status) {
        params.push(`status=${encodeURIComponent(filters.status)}`);
      }
      if (filters.creationDate) {
        params.push(`created_before=${encodeURIComponent(filters.creationDate)}`);
      }
      if (filters.assignedTo) {
        params.push(`assigned_to=${encodeURIComponent(filters.assignedTo)}`);
      }
      params.push("per_page=1000");
      params.push("page=1");
      const queryString = params.length ? `?${params.join("&")}` : "";
      const url = `${API}ticket/tickets/${queryString}`;
      const response = await axios.get(url, { headers });
      let tickets = response.data.data || response.data.results || response.data.tickets || [];
      if (selectedTicketType === "Tickets") {
        tickets = tickets.filter((t) => t.category_name?.toLowerCase() !== "portal");
      } else if (selectedTicketType === "Portal") {
        tickets = tickets.filter((t) => t.category_name?.toLowerCase() === "portal");
      }
      setTicketsData(tickets);
    } catch (err) {
      console.error("Fetch Tickets Error:", err);
      if (retryCount < 2) {
        setTimeout(() => fetchTickets(filters, retryCount + 1), 1000);
      } else {
        setError("Error fetching tickets. Please try again.");
        setTicketsData([]);
      }
    } finally {
      setDataLoading(false);
    }
  }, 300);

  useEffect(() => {
    if (filteredTickets.length > 0 || !dataLoading) {
      setTotalTickets(filteredTickets.length);
      setDashboardStats({
        total: filteredTickets.length,unassigned: filteredTickets.filter((t) => !t.assigned_to).length,
        new: filteredTickets.filter((t) => t.status?.toLowerCase() === "new").length,
        open: filteredTickets.filter((t) => t.status?.toLowerCase() === "open").length,
        resolved: filteredTickets.filter((t) => t.status?.toLowerCase() === "resolved").length,
        closed: filteredTickets.filter((t) => t.status?.toLowerCase() === "closed").length,
        escalated: filteredTickets.filter((t) => t.status?.toLowerCase() === "escalated").length,
      });
    }
  }, [filteredTickets, dataLoading]);

  useEffect(() => {
    const savedFilters = sessionStorage.getItem("ticketFilters");
    const filtersToUse = savedFilters ? JSON.parse(savedFilters) : currentFilters;
    fetchTickets(filtersToUse);
  }, [currentFilters, selectedTicketType]);

  const handleTileClick = (label) => {
    let filterValue;
    if (label === "Total") {
      filterValue = "my-tickets";
      setCurrentFilters((prev) => {
        const newFilters = { ...prev, status: undefined };
        sessionStorage.setItem("ticketFilters", JSON.stringify(newFilters));
        return newFilters;
      });
    } else {
      filterValue = label.toLowerCase();
      if (label === "Unassigned") {
        filterValue = "unassigned";
      }
      setCurrentFilters((prev) => {
        const newFilters = { ...prev, status: filterValue };
        sessionStorage.setItem("ticketFilters", JSON.stringify(newFilters));
        return newFilters;
      });
    }
    setStatusFilter(filterValue);
    sessionStorage.setItem("statusFilter", filterValue);
  };

  const handleApplyFilters = () => {
    const filters = {
      customerName:
        selectedCustomer && (selectedCustomer.name || selectedCustomer.client_name),
      priority: selectedPriority && selectedPriority.value,
      status: selectedStatus && selectedStatus.value,
      creationDate: selectedCreationDate,
      assignedTo:
        selectedAssignedTo &&
        (selectedAssignedTo.id || selectedAssignedTo.email || selectedAssignedTo.assigned_to_name),
    };
    console.log("Filters applied:", filters);
    setCurrentFilters(filters);
    setPaneOpen(false);

    sessionStorage.setItem("ticketFilters", JSON.stringify(filters));
    sessionStorage.setItem("selectedTicketType", selectedTicketType);
    sessionStorage.setItem("statusFilter", statusFilter);
  };

  const handleClearFilters = () => {
    setSelectedCustomer(null);
    setSelectedPriority(null);
    setSelectedStatus(null);
    setSelectedCreationDate("");
    setSelectedAssignedTo(null);
    setStatusFilter("my-tickets");
    setCurrentFilters({});
    setSelectedTicketType("All");

    sessionStorage.removeItem("ticketFilters");
    sessionStorage.removeItem("selectedTicketType");
    sessionStorage.removeItem("statusFilter");
  };

  const handleCustomerInputChange = (value) => {
    setCustomerInputValue(value);
    setIsLoadingCustomer(true);
    const customers = [];
    const seen = new Set();
    ticketsData.forEach((ticket) => {
      const email = ticket.client_name;
      const name = ticket.created_by_name;
      if (
        email &&
        !seen.has(email) &&
        (!value ||
          email.toLowerCase().includes(value.toLowerCase()) ||
          (name && name.toLowerCase().includes(value.toLowerCase())))
      ) {
        seen.add(email);
        customers.push({
          customer_name: name || email,
          email: email,
          client_name: email,
        });
      }
    });
    if (
      selectedCustomer &&
      !customers.find(
        (c) => c.client_name === (selectedCustomer.client_name || selectedCustomer.name)
      )
    ) {
      customers.push({
        customer_name: selectedCustomer.customer_name || selectedCustomer.client_name,
        email: selectedCustomer.client_name,
        client_name: selectedCustomer.client_name,
      });
    }
    setCustomerOptions(customers.slice(0, 20));
    setIsLoadingCustomer(false);
  };

  const handleAssignedToInputChange = (value) => {
    setAssignedToInputValue(value);
    setIsLoadingAssignedTo(true);
    const assigned = [];
    const seen = new Set();
    ticketsData.forEach((ticket) => {
      const assignedEmail = ticket.assigned_to_name;
      const assignedName = ticket.assigned_to_name;
      if (
        assignedEmail &&
        !seen.has(assignedEmail) &&
        (!value ||
          assignedEmail.toLowerCase().includes(value.toLowerCase()) ||
          (assignedName && assignedName.toLowerCase().includes(value.toLowerCase())))
      ) {
        seen.add(assignedEmail);
        assigned.push({
          assigned_name: assignedName || assignedEmail,
          email: assignedEmail,
          assigned_to_name: assignedEmail,
          id: ticket.assigned_to,
        });
      }
    });
    if (
      selectedAssignedTo &&
      !assigned.find(
        (a) => a.assigned_to_name === (selectedAssignedTo.assigned_to_name || selectedAssignedTo.email)
      )
    ) {
      assigned.push({
        assigned_name: selectedAssignedTo.assigned_name || selectedAssignedTo.email,
        email: selectedAssignedTo.assigned_to_name || selectedAssignedTo.email,
        assigned_to_name: selectedAssignedTo.assigned_to_name || selectedAssignedTo.email,
        id: selectedAssignedTo.id,
      });
    }
    setAssignedToOptions(assigned.slice(0, 20));
    setIsLoadingAssignedTo(false);
  };

  if (dataLoading) {
    return (
      <div className="bg-light min-vh-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <div className="spinner-border text-secondary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading tickets...</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-light min-vh-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error!</h4>
            <p>{error}</p>
            <button
              className="btn btn-primary"
              onClick={() => {
                const savedFilters = sessionStorage.getItem("ticketFilters");
                fetchTickets(savedFilters ? JSON.parse(savedFilters) : {});
              }}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-light min-vh-100">
      <main>
        <div className="container-fluid py-4">
          <div className="row d-flex align-items-center justify-content-between">
            <div className="col-auto">
              <select
                className="form-select"
                value={selectedTicketType}
                onChange={(e) => {
                  setSelectedTicketType(e.target.value);
                  sessionStorage.setItem("selectedTicketType", e.target.value);
                }}
                style={{ width: "200px", padding: "8px" }}
              >
                <option value="Tickets">Tickets</option>
                <option value="Portal">Portal</option>
                <option value="All">All</option>
              </select>
            </div>
            <div className="col-auto">
              <button className="btn btn-primary" onClick={() => setShowCreateTicket(true)}>
                + Create New Ticket
              </button>
            </div>
          </div>
        </div>
        {!showCreateTicket && (
          <div className="container-fluid">
            {/*  remove the fixed “row-cols-5”  */}
            <div className="row g-3">
              {[
                { label: "Total", value: totalTickets },
                { label: "Unassigned", value: dashboardStats.unassigned },
                { label: "Open", value: dashboardStats.open },
                { label: "Escalated", value: dashboardStats.escalated },
                { label: "Resolved", value: dashboardStats.resolved },
              ].map((stat) => {
                const isSelected =
                  statusFilter ===
                  (stat.label === "Total" ? "my-tickets" : stat.label.toLowerCase());

                return (
                  <div className="col" key={stat.label}>
                    <div
                      className={`card text-center shadow-sm ${isSelected ? "border-secondary" : ""}`}
                      onClick={() => handleTileClick(stat.label)}
                      style={{ cursor: "pointer" }}
                    >
                      <div className="card-body">
                        <div className="card-title text-muted text-uppercase small">
                          {stat.label}
                        </div>
                        <div
                          className={`hyperlink ${stat.color}`}
                          style={{ fontSize: "30px" }}
                        >
                          {stat.value}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {showCreateTicket && (
          <div className="mb-4">
            <CreateTicket
              open={showCreateTicket}
              onClose={() => {
                setShowCreateTicket(false);
                setTicketPrefill(null);
                const savedFilters = sessionStorage.getItem("ticketFilters");
                fetchTickets(savedFilters ? JSON.parse(savedFilters) : {});
              }}
              inline={true}
              initialTitle={ticketPrefill?.title}
              initialDescription={ticketPrefill?.description}
            />
          </div>
        )}

        <SlidingPane
          className="some-custom-class"
          overlayClassName="some-custom-overlay-class"
          isOpen={paneOpen}
          title="Filter Tickets"
          subtitle=""
          onRequestClose={() => setPaneOpen(false)}
        >
          <div className="row py-2">
            <div className="col-xl-12">
              <div className="form-group row">
                <label className="col-lg-12 col-form-label">{t("Customer Name")}</label>
                <div className="col-lg-12">
                  <Select
                    value={selectedCustomer}
                    filterOption={false}
                    options={customerOptions}
                    isClearable
                    onInputChange={handleCustomerInputChange}
                    getOptionLabel={(o) => o.customer_name || o.created_by_name}
                    getOptionValue={(o) => o.customer_name || o.created_by_name}
                    isLoading={isLoadingCustomer}
                    placeholder={t("Search for Customer Name")}
                    onChange={(option) => {
                      setSelectedCustomer(option);
                      sessionStorage.setItem(
                        "selectedCustomer",
                        JSON.stringify(option)
                      );
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="col-xl-12">
              <div className="form-group row">
                <label className="col-lg-12 col-form-label">{t("Priority")}</label>
                <div className="col-lg-12">
                  <Select
                    value={selectedPriority}
                    options={priorityOptions}
                    isClearable
                    placeholder={t("Select Priority")}
                    onChange={(option) => {
                      setSelectedPriority(option);
                      sessionStorage.setItem(
                        "selectedPriority",
                        JSON.stringify(option)
                      );
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="col-xl-12">
              <div className="form-group row">
                <label className="col-lg-12 col-form-label">{t("Status")}</label>
                <div className="col-lg-12">
                  <Select
                    value={selectedStatus}
                    options={statusOptions}
                    isClearable
                    placeholder={t("Select Status")}
                    onChange={(option) => {
                      setSelectedStatus(option);
                      sessionStorage.setItem(
                        "selectedStatus",
                        JSON.stringify(option)
                      );
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="col-xl-12">
              <div className="form-group row">
                <label className="col-lg-12 col-form-label">{t("Creation Date (Before)")}</label>
                <div className="col-lg-12">
                  <input
                    type="date"
                    className="form-control"
                    value={selectedCreationDate}
                    onChange={(e) => {
                      setSelectedCreationDate(e.target.value);
                      sessionStorage.setItem("selectedCreationDate", e.target.value);
                    }}
                    placeholder={t("Select date to show tickets created before this date")}
                  />
                </div>
              </div>
            </div>
            {hasViewAllTicketsPermission && (
              <div className="col-xl-12">
                <div className="form-group row">
                  <label className="col-lg-12 col-form-label">{t("Assigned To")}</label>
                  <div className="col-lg-12">
                    <Select
                      value={selectedAssignedTo}
                      filterOption={false}
                      options={assignedToOptions}
                      isClearable
                      onInputChange={handleAssignedToInputChange}
                      getOptionLabel={(o) => o.assigned_name || o.email}
                      getOptionValue={(o) => o.email || o.assigned_to_name}
                      isLoading={isLoadingAssignedTo}
                      placeholder={t("Search for Assigned User")}
                      onChange={(option) => {
                        setSelectedAssignedTo(option);
                        sessionStorage.setItem(
                          "selectedAssignedTo",
                          JSON.stringify(option)
                        );
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="col-xl-12">
              <div className="form-group row">
                <div className="col-lg-12 text-left" style={{ marginTop: "30px" }}>
                  <button
                    type="button"
                    className="clearReport btn bg-gradient-secondary ms-auto mb-0"
                    onClick={handleClearFilters}
                  >
                    {t("Reset")}
                  </button>
                  &nbsp;&nbsp;
                  <button
                    type="button"
                    className="filterReport add btn btn-primary ms-auto mb-0"
                    onClick={handleApplyFilters}
                  >
                    {t("Apply")}
                  </button>
                  &nbsp;&nbsp;
                </div>
              </div>
            </div>
          </div>
        </SlidingPane>

        {!showCreateTicket && (
          <div className="container-fluid shadow-sm">
            <div className="p-0">
              <MaterialTable
                key={statusFilter}
                tableRef={tableTicketRef}
                style={{
                  backgroundColor: "var(--bs-table-bg)",
                  borderBottomWidth: "1px",
                  boxShadow: "inset 0 0 0 9999px var(--bs-table-accent-bg)",
                }}
                title={<h5 className="mb-0">{t("")}</h5>}
                components={{
                  Toolbar: (props) => (
                    <div className="MuiToolbar-root MTableToolbar-spacer-21 my-temp-class">
                      <MTableToolbar {...props} />
                      <span
                        onClick={() => setPaneOpen(true)}
                        style={{
                          color: "#b4b7c5",
                          paddingRight: "1px",
                          cursor: "pointer",
                        }}
                      >
                        <img
                          src={process.env.PUBLIC_URL + "/static/images/ic-filter.svg"}
                          alt="Filter"
                        />
                      </span>
                    </div>
                  ),
                }}
                columns={[
                  {
                    title: `${t("Ticket ID")}`,
                    field: "ticket_id",
                    headerStyle: {
                      verticalAlign: "inherit",
                      fontWeight: "bold",
                      textAlign: "left",
                    },
                    cellStyle: {
                      textAlign: "left",
                      fontSize: 12,
                    },
                    render: (rowData) => (
                      <>
                        <div className="d-xl-none tc d-md-none d-lg-none">
                          {t("Ticket ID")}
                        </div>
                        <div
                          className="hyperlink"
                          onClick={() => {
                            const ticketIdentifier = rowData.ticket_id || rowData.id;
                            sessionStorage.setItem("selectedTicketId", ticketIdentifier);
                            navigate(`/editcompanyticket/?ticket_id=${ticketIdentifier}`);
                          }}
                        >
                          {rowData.ticket_id || rowData.id}
                        </div>
                      </>
                    ),
                  },
                  {
                    title: `${t("Create Date")}`,
                    field: "created_at",
                    headerStyle: {
                      verticalAlign: "inherit",
                      fontWeight: "bold",
                      textAlign: "left",
                    },
                    cellStyle: {
                      textAlign: "left",
                      fontSize: 12,
                    },
                    render: (rowData) => (
                      <>
                        <div className="d-xl-none tc d-md-none d-lg-none">
                          {t("Create Date")}
                        </div>
                        <div>
                          {rowData.created_at
                            ? moment(rowData.created_at).format("MM/DD/YYYY")
                            : "-"}
                        </div>
                      </>
                    ),
                  },
                  {
                    title: `${t("Due Date")}`,
                    field: "due_date",
                    headerStyle: {
                      verticalAlign: "inherit",
                      fontWeight: "bold",
                      textAlign: "left",
                    },
                    cellStyle: {
                      textAlign: "left",
                      fontSize: 12,
                    },
                    render: (rowData) => (
                      <>
                        <div className="d-xl-none tc d-md-none d-lg-none">
                          {t("Due Date")}
                        </div>
                        <div>
                          {rowData.due_date
                            ? moment(rowData.due_date).format("MM/DD/YYYY")
                            : "-"}
                        </div>
                      </>
                    ),
                  },
                  {
                    title: `${t("Category")}`,
                    field: "category_name",
                    headerStyle: {
                      verticalAlign: "inherit",
                      fontWeight: "bold",
                      textAlign: "left",
                    },
                    cellStyle: {
                      textAlign: "left",
                      fontSize: 12,
                    },
                    render: (rowData) => (
                      <>
                        <div className="d-xl-none tc d-md-none d-lg-none">
                          {t("Category")}
                        </div>
                        <div>{rowData.category_name}</div>
                      </>
                    ),
                  },
                  {
                    title: `${t("Status")}`,
                    field: "status",
                    headerStyle: {
                      verticalAlign: "inherit",
                      fontWeight: "bold",
                      textAlign: "left",
                    },
                    cellStyle: {
                      textAlign: "left",
                      fontSize: 12,
                    },
                    render: (rowData) => (
                      <>
                        <div className="d-xl-none tc d-md-none d-lg-none">
                          {t("Status")}
                        </div>
                        <span className={`badge ${statusColors[rowData.status] || "bg-secondary"}`}>
                          {rowData.status === "unassigned" ? "Unassigned" : (rowData.status_display || rowData.status)}
                        </span>
                      </>
                    ),
                  },
                  {
                    title: `${t("User")}`,
                    field: "client_name",
                    headerStyle: {
                      verticalAlign: "inherit",
                      fontWeight: "bold",
                      textAlign: "left",
                    },
                    cellStyle: {
                      textAlign: "left",
                      fontSize: 12,
                    },
                    render: (rowData) => (
                      <>
                        <div className="d-xl-none tc d-md-none d-lg-none">
                          {t("Client")}
                        </div>
                        <div>{rowData.client_name || "-"}</div>
                      </>
                    ),
                  },
                  {
                    title: `${t("Subject")}`,
                    field: "title",
                    headerStyle: {
                      verticalAlign: "inherit",
                      fontWeight: "bold",
                      textAlign: "left",
                    },
                    cellStyle: {
                      textAlign: "left",
                      fontSize: 12,
                    },
                    render: (rowData) => (
                      <>
                        <div className="d-xl-none tc d-md-none d-lg-none">
                          {t("Subject")}
                        </div>
                        <div>{rowData.title}</div>
                      </>
                    ),
                  },
                  {
                    title: `${t("Priority")}`,
                    field: "priority",
                    headerStyle: {
                      verticalAlign: "inherit",
                      fontWeight: "bold",
                      textAlign: "left",
                    },
                    cellStyle: {
                      textAlign: "left",
                      fontSize: 12,
                    },
                    render: (rowData) => (
                      <>
                        <div className="d-xl-none tc d-md-none d-lg-none">
                          {t("Priority")}
                        </div>
                        <span className={`badge ${priorityColors[rowData.priority] || "bg-secondary"}`}>
                          {rowData.priority}
                        </span>
                      </>
                    ),
                  },
                  {
                    title: `${t("Assigned To")}`,
                    field: "assigned_to_name",
                    headerStyle: {
                      verticalAlign: "inherit",
                      fontWeight: "bold",
                      textAlign: "left",
                    },
                    cellStyle: {
                      textAlign: "left",
                      fontSize: 12,
                    },
                    render: (rowData) => (
                      <>
                        <div className="d-xl-none tc d-md-none d-lg-none">
                          {t("Assigned To")}
                        </div>
                        <div>{rowData.assigned_to_name || "-"}</div>
                      </>
                    ),
                  },
                ]}
                data={filteredTickets}
                options={{
                  headerStyle: {
                    backgroundColor: "white",
                    color: "#245C7F",
                    fontSize: 12,
                  },
                  rowStyle: (rowData, index) => {
                    if (index % 2) {
                      return { backgroundColor: "#fff" };
                    }
                  },
                  sorting: true,
                  search: true,
                  toolbar: true,
                  paging: true,
                  pageSize: 10,
                  emptyRowsWhenPaging: false,
                  pageSizeOptions: [10, 20, 25, 50],
                  paginationType: "stepped",
                  showFirstLastPageButtons: false,
                  exportAllData: true,
                  exportButton: true,
                  exportFileName: "Support_Tickets",
                  actionsColumnIndex: -1,
                  columnsButton: true,
                }}
              />
            </div>
          </div>
        )}
        <ToastContainer />
      </main>
    </div>
  );
}